import { detectPageType } from './vmkQuoteTable';
import { addBtwToExVatPrice, getQuoteProjectFeesExVat } from './pdfPricing';

export const PAGE_TYPE_ROUTES = {
  kozijnen: '/kozijnen',
  voordeur: '/voordeur',
  achterdeur: '/achterdeur',
  terrasdeur: '/terrasdeur',
  schuifpui: '/schuifpui',
  keralit: '/keralit',
};

export const getRouteForQuoteType = (type) =>
  PAGE_TYPE_ROUTES[type] || PAGE_TYPE_ROUTES.kozijnen;

export const getRouteForDrawing = (drawing) => {
  const type = drawing?.type || detectPageType(drawing?.config || {});
  return getRouteForQuoteType(type);
};

/** Normalize backend quote_data into local savedQuotes shape. */
export const normalizeBackendQuoteDrawings = (quoteData = {}) => {
  const raw =
    quoteData.drawings ||
    quoteData.savedQuotes ||
    (quoteData.config
      ? [
          {
            config: quoteData.config,
            pricing: quoteData.pricing,
            dimensions: quoteData.dimensions,
            svgData: quoteData.svgData,
            type: quoteData.type,
          },
        ]
      : []);

  return raw.map((drawing, index) => ({
    ...drawing,
    id: drawing.id || `drawing-${index}-${Date.now()}`,
    type: drawing.type || detectPageType(drawing.config || {}),
    savedAt: drawing.savedAt || new Date().toISOString(),
  }));
};

export const defaultClientDetails = () => ({
  name: '',
  address: '',
  city: '',
  postalCode: '',
  phone: '',
  email: '',
  notes: '',
  signature: '',
  subjectToFinance: false,
  clickOnColorAndSize: false,
  screenElectricallyOperated: false,
  rollerShutterElectricallyOperated: false,
  buoySharing: false,
  typesetting: false,
  container: false,
  interiorFinish: false,
  crane: false,
  craneTrips: '',
  liftingAssistance: false,
  liftingAssistancePersonnel: '',
  comments: '',
});

/** True when the user explicitly opened a saved backend quote for editing (not a stale localStorage session). */
export const isEditingBackendQuote = (session) =>
  Boolean(session?.id && session?.isEditingSession === true);

/**
 * Grand total incl. 21% BTW from configurator-saved drawings.
 * Uses stored unit prices (already reflect Summary adjustments) and applies BTW once,
 * matching the orange Total on the configurator — no second VAT pass.
 */
export const computeStoredQuoteTotalInclVat = (drawings = [], clientDetails = null) => {
  const exVatSubtotal = drawings.reduce((sum, quote) => {
    const qty = quote.config?.quantity || 1;
    return sum + (Number(quote.pricing?.totalPrice) || 0) * qty;
  }, 0);
  const projectExVat = getQuoteProjectFeesExVat(clientDetails);
  return addBtwToExVatPrice(exVatSubtotal + projectExVat);
};

/** Parse clientDetails.contactId for API payloads; omit invalid/stale ids. */
export const resolveQuoteContactId = (clientDetails, knownContactIds = null) => {
  const raw = clientDetails?.contactId;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  if (Array.isArray(knownContactIds) && knownContactIds.length > 0) {
    return knownContactIds.includes(parsed) ? parsed : undefined;
  }
  return parsed;
};

export const normalizeMemberId = (id) =>
  id == null || id === '' ? '' : String(id);

export const findTeamMemberById = (teamMembers, memberId) =>
  teamMembers.find((m) => normalizeMemberId(m.id) === normalizeMemberId(memberId));

export const buildTeamMemberLookup = (teamMembers = []) => {
  const map = new Map();
  teamMembers.forEach((member) => {
    const key = normalizeMemberId(member.id);
    if (key) map.set(key, member);
  });
  return map;
};

/** Prefer the quotes.created_by FK from API; ignore stale quote_data.quoteCreator for display. */
export const resolveQuoteCreatedById = (quote) => {
  const raw = quote?.created_by ?? quote?.createdBy ?? null;
  if (raw == null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatTeamMemberCreator = (member) => {
  const name =
    `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || '';
  return { name, email: member.email || '', id: member.id };
};

const normalizeEmail = (email) => String(email || '').toLowerCase().trim();

/** Creator snapshot stored in quote_data at save time (source of truth when present). */
export const getStoredQuoteCreator = (quote) => {
  const stored = quote?.quote_data?.quoteCreator;
  if (!stored || typeof stored !== 'object') return null;
  return {
    id: stored.id ?? null,
    email: stored.email || '',
    first_name: stored.first_name || '',
    last_name: stored.last_name || '',
  };
};

export const buildQuoteCreatorPayload = (user) => {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
  };
};

/** Attach quoteCreator for new saves; preserve existing creator when editing. */
export const withQuoteCreator = (quoteData, user, existingQuoteData = null) => {
  const next = { ...(quoteData || {}) };
  if (existingQuoteData?.quoteCreator) {
    next.quoteCreator = existingQuoteData.quoteCreator;
  } else if (!next.quoteCreator && user) {
    next.quoteCreator = buildQuoteCreatorPayload(user);
  }
  return next;
};

export const getQuoteCreatorInfo = (quote, teamMembers = []) => {
  const createdById = resolveQuoteCreatedById(quote);
  if (createdById != null && teamMembers.length > 0) {
    const member = findTeamMemberById(teamMembers, createdById);
    if (member) return formatTeamMemberCreator(member);
  }

  const apiName = `${quote.creator_first_name || ''} ${quote.creator_last_name || ''}`.trim();
  const apiEmail = quote.creator_email || '';
  if ((apiName || apiEmail) && createdById == null) {
    return { name: apiName || apiEmail, email: apiEmail, id: quote.created_by ?? null };
  }

  if (createdById != null) {
    if (apiName || apiEmail) {
      return { name: apiName || apiEmail, email: apiEmail, id: createdById };
    }
    return { name: `User #${createdById}`, email: '', id: createdById };
  }

  const stored = getStoredQuoteCreator(quote);
  if (stored) {
    const name = `${stored.first_name || ''} ${stored.last_name || ''}`.trim();
    if (name || stored.email) {
      return { name: name || stored.email, email: stored.email, id: stored.id };
    }
  }

  const member = teamMembers.find(
    (m) => normalizeEmail(m.email) === normalizeEmail(quote.creator_email)
  );
  if (!member) return null;
  return formatTeamMemberCreator(member);
};

/** Match quotes to team members by created_by (DB), then email, then stored snapshot. */
export const quoteBelongsToTeamMember = (quote, memberOrId, teamMembers = []) => {
  const member =
    typeof memberOrId === 'object' && memberOrId != null
      ? memberOrId
      : findTeamMemberById(teamMembers, memberOrId);
  const memberId = normalizeMemberId(member?.id ?? memberOrId);
  if (!memberId) return false;

  const memberEmail = normalizeEmail(member?.email);
  const createdById = resolveQuoteCreatedById(quote);

  if (createdById != null && normalizeMemberId(createdById) === memberId) return true;

  const creatorEmail = normalizeEmail(quote.creator_email);
  if (creatorEmail && memberEmail && creatorEmail === memberEmail) return true;

  const stored = getStoredQuoteCreator(quote);
  if (stored?.id != null && normalizeMemberId(stored.id) === memberId) return true;
  if (stored?.email && memberEmail && normalizeEmail(stored.email) === memberEmail) return true;

  return false;
};

export const countQuotesForTeamMember = (quotes, member, teamMembers = []) =>
  quotes.filter((quote) => quoteBelongsToTeamMember(quote, member, teamMembers)).length;
